import React, { useEffect, useRef, useState, useCallback } from 'react'
import { Play, Pause } from 'lucide-react'

import { prepCanvas } from './canvasUtil'

interface Props { title?: string; description?: string; onComplete?: () => void }
interface P { x: number; y: number; vx: number; vy: number; hx: number; hy: number }

// Vẽ hạt dạng cầu 3D (gradient toả sáng + viền tối) cho đẹp & trung thực.
function drawSphere(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, hi: string, base: string, edge: string) {
  const g = ctx.createRadialGradient(x - r * 0.35, y - r * 0.35, r * 0.12, x, y, r)
  g.addColorStop(0, hi)
  g.addColorStop(0.45, base)
  g.addColorStop(1, edge)
  ctx.fillStyle = g
  ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill()
}

// Sự chuyển thể (Lớp 12 — Bài 1): nhiệt độ ↑ → rắn → lỏng → khí.
// Rắn: hạt dao động quanh nút mạng. Lỏng: liên kết lỏng lẻo, trượt. Khí: tự do, lấp đầy bình.
const PhaseChangeSimulation: React.FC<Props> = ({
  title = 'Sự chuyển thể của chất',
  description = 'Tăng nhiệt độ để quan sát chuyển từ thể rắn (mạng tinh thể) → lỏng → khí.',
  onComplete,
}) => {
  const cv = useRef<HTMLCanvasElement>(null)
  const raf = useRef<number | null>(null)
  const ps = useRef<P[]>([])
  const [running, setRunning] = useState(true)
  const [temp, setTemp] = useState(2)
  const firstRef = useRef(false)
  const W = 460, H = 280

  const phase = temp < 4 ? 'rắn' : temp < 7 ? 'lỏng' : 'khí'

  const build = useCallback(() => {
    const cols = 10, rows = 6, gap = 34, ox = 80, oy = 60
    const arr: P[] = []
    for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
      const hx = ox + c * gap, hy = oy + r * gap
      arr.push({ x: hx, y: hy, vx: 0, vy: 0, hx, hy })
    }
    ps.current = arr
  }, [])
  useEffect(() => { build() }, [build])

  const tick = useCallback(() => {
    const ctx = prepCanvas(cv.current, W, H); if (!ctx) return
    ctx.clearRect(0, 0, W, H)
    ctx.strokeStyle = '#94a3b8'; ctx.lineWidth = 2; ctx.strokeRect(6, 6, W - 12, H - 12)
    const k = temp < 4 ? 0.18 : temp < 7 ? 0.012 : 0
    const damp = temp < 4 ? 0.86 : temp < 7 ? 0.95 : 0.997
    const kick = temp * 0.22
    for (const p of ps.current) {
      p.vx += (p.hx - p.x) * k + (Math.random() - 0.5) * kick
      p.vy += (p.hy - p.y) * k + (Math.random() - 0.5) * kick
      p.vx *= damp; p.vy *= damp
      p.x += p.vx; p.y += p.vy
      if (p.x < 12) { p.x = 12; p.vx = Math.abs(p.vx) }
      if (p.x > W - 12) { p.x = W - 12; p.vx = -Math.abs(p.vx) }
      if (p.y < 12) { p.y = 12; p.vy = Math.abs(p.vy) }
      if (p.y > H - 12) { p.y = H - 12; p.vy = -Math.abs(p.vy) }
      if (temp < 4) drawSphere(ctx, p.x, p.y, 7, '#bfdbfe', '#3b82f6', '#1e3a8a')
      else if (temp < 7) drawSphere(ctx, p.x, p.y, 7, '#a5f3fc', '#06b6d4', '#0e7490')
      else drawSphere(ctx, p.x, p.y, 7, '#fed7aa', '#f97316', '#9a3412')
    }
    raf.current = requestAnimationFrame(tick)
  }, [temp])

  useEffect(() => {
    if (running) { if (!firstRef.current) { firstRef.current = true; onComplete?.() } raf.current = requestAnimationFrame(tick) }
    else if (raf.current) cancelAnimationFrame(raf.current)
    return () => { if (raf.current) cancelAnimationFrame(raf.current) }
  }, [running, tick, onComplete])

  return (
    <div className="rounded-2xl border border-orange-200 bg-white p-4 my-3">
      <h4 className="font-bold text-orange-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <canvas ref={cv} width={W} height={H} className="w-full rounded-lg border border-gray-100 bg-slate-50" />
      <div className="flex items-center justify-center gap-2 my-3">
        <span className="text-sm text-gray-500">Trạng thái hiện tại:</span>
        <span className={`px-3 py-1 rounded-full font-bold text-white ${phase === 'rắn' ? 'bg-blue-600' : phase === 'lỏng' ? 'bg-cyan-600' : 'bg-orange-500'}`}>Thể {phase}</span>
      </div>
      <label className="flex items-center gap-2 text-sm"><span className="w-24 text-gray-600">Nhiệt độ</span>
        <input type="range" min={1} max={10} value={temp} onChange={(e) => setTemp(Number(e.target.value))} className="flex-1" />
        <span className="w-10 text-right font-semibold">{temp}</span></label>
      <button onClick={() => setRunning((r) => !r)} className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-orange-500 text-white font-bold hover:bg-orange-600">
        {running ? <><Pause className="w-4 h-4" /> Tạm dừng</> : <><Play className="w-4 h-4" /> Chạy</>}</button>
    </div>
  )
}
export default PhaseChangeSimulation
